import { Alert, AlertIcon, Box, Button, Flex, Input, Text, Textarea, useDisclosure } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { ActionButton } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { EmptyState } from '../../../../components/EmptyState'
import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import { LoadingBlock } from '../../../../components/Loading'
import { StatCard } from '../../../../components/SectionCard'
import { ApiErrorShape, formatDateTime, useAppToast } from '../../../../helpers'
import { EventSurveyService } from '../../../../services/Event/EventSurveyService'
import { IEventSurveyDeliveryPreview, TEventSurveyDeliveryMode } from '../../../../services/Event/surveyTypes'
import { renderEmailSample, SAMPLE_RECIPIENT_NAME } from './eventSurveyForm'

interface IEventSurveySendModalProps {
  isOpen: boolean
  onClose: () => void
  eventId: number
  eventName: string
  onDispatched?: () => void
}

const INITIAL_SUBJECT = 'Pesquisa de Satisfação — #NOME_EVENTO'
const INITIAL_BODY = `Olá, #PRIMEIRO_NOME!

Gostaríamos de saber como foi a sua experiência no #NOME_EVENTO. Sua resposta nos ajuda a melhorar os próximos eventos da ABRA.

Responda à pesquisa pelo seu link individual:
#LINK_PESQUISA

Obrigado pela participação!`
const REMINDER_SUBJECT = 'Lembrete: Pesquisa de Satisfação — #NOME_EVENTO'
const REMINDER_BODY = `Olá, #PRIMEIRO_NOME!

Ainda dá tempo de contar como foi a sua experiência no #NOME_EVENTO. Se puder, responda à pesquisa pelo seu link individual:
#LINK_PESQUISA

Obrigado pela participação!`

/** Envio independente do certificado, com prévia e lembrete só para quem ainda não respondeu. */
export default function EventSurveySendModal({
  isOpen,
  onClose,
  eventId,
  eventName,
  onDispatched
}: IEventSurveySendModalProps) {
  const service = useMemo(() => new EventSurveyService(), [])
  const toast = useAppToast()
  const confirm = useDisclosure()
  const [preview, setPreview] = useState<IEventSurveyDeliveryPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [sending, setSending] = useState(false)
  const [mode, setMode] = useState<TEventSurveyDeliveryMode>('initial')
  const [subject, setSubject] = useState(INITIAL_SUBJECT)
  const [body, setBody] = useState(INITIAL_BODY)
  const [errors, setErrors] = useState<{ subject?: string; body?: string; mode?: string }>({})

  const load = () => {
    setLoading(true)
    setLoadError(false)
    service
      .deliveryPreview(eventId)
      .then(setPreview)
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar os destinatários da pesquisa', error)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!isOpen) return
    setMode('initial')
    setSubject(INITIAL_SUBJECT)
    setBody(INITIAL_BODY)
    setErrors({})
    load()
  }, [isOpen, eventId])

  const chooseMode = (next: TEventSurveyDeliveryMode) => {
    setMode(next)
    setSubject(next === 'initial' ? INITIAL_SUBJECT : REMINDER_SUBJECT)
    setBody(next === 'initial' ? INITIAL_BODY : REMINDER_BODY)
    setErrors({})
  }

  const sampleText = (value: string) => renderEmailSample(value, eventName, preview?.example_url)

  const handleSubmit = () => {
    const nextErrors: typeof errors = {}
    if (!subject.trim()) nextErrors.subject = 'Informe o assunto.'
    if (!body.trim()) nextErrors.body = 'Informe o corpo do e-mail.'
    else if (!body.includes('#LINK_PESQUISA')) nextErrors.body = 'Inclua #LINK_PESQUISA no corpo do e-mail.'
    if (subject.includes('#LINK_PESQUISA')) nextErrors.subject = 'O link individual deve ficar no corpo do e-mail.'
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length === 0) confirm.onOpen()
  }

  const handleDispatch = async () => {
    setSending(true)
    try {
      const result = await service.dispatch(eventId, { mode, subject, body })
      toast.success('Envio da pesquisa iniciado', `${result.dispatched} e-mail(s) foram colocados na fila.`)
      confirm.onClose()
      onClose()
      onDispatched?.()
    } catch (error) {
      const apiErrors = (error as ApiErrorShape).response?.data?.errors ?? {}
      setErrors({
        subject: apiErrors.subject?.[0],
        body: apiErrors.body?.[0],
        mode: apiErrors.mode?.[0]
      })
      confirm.onClose()
      toast.error('Não foi possível iniciar o envio da pesquisa', error)
      // Os contadores podem ter mudado (alguém respondeu, a pesquisa foi desativada): a próxima
      // tentativa confirma com os números de agora.
      load()
    } finally {
      setSending(false)
    }
  }

  const count = preview?.[mode] ?? 0
  const canSend =
    !!preview && !loading && !loadError && preview.survey_active && preview.can_send_now && count > 0 && !sending

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title="Enviar pesquisa NPS"
        size="wide"
        onSubmit={handleSubmit}
        confirmAction="send"
        confirmLabel={mode === 'initial' ? 'Enviar pesquisa' : 'Enviar lembrete'}
        isConfirmDisabled={!canSend}
        noValidate
      >
        {loading ? (
          <LoadingBlock entity="destinatários" />
        ) : loadError || !preview ? (
          <EmptyState
            tone="error"
            title="Não foi possível carregar os destinatários"
            action={<ActionButton action="refresh" label="Tentar novamente" showLabel mr={0} onClick={load} />}
          />
        ) : (
          <Flex direction="column" gap={4}>
            {!preview.survey_active && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                Ative a pesquisa antes de enviar os links.
              </Alert>
            )}
            {!preview.can_send_now && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />O envio abre após o término do evento
                {preview.opens_at ? `, em ${formatDateTime(preview.opens_at)}` : ''}.
              </Alert>
            )}
            <Text fontSize="sm">
              O primeiro envio alcança quem ainda não recebeu o link, inclusive quem já recebeu o certificado sem
              pesquisa. O lembrete alcança quem recebeu o link, ainda não respondeu e não recebeu outro e-mail nas
              últimas 24 horas.
            </Text>
            <Flex gap={2} wrap="wrap">
              <Button
                type="button"
                size="sm"
                variant={mode === 'initial' ? 'solid' : 'outline'}
                colorScheme="magenta"
                onClick={() => chooseMode('initial')}
              >
                Primeiro envio ({preview.initial})
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mode === 'reminder' ? 'solid' : 'outline'}
                colorScheme="magenta"
                onClick={() => chooseMode('reminder')}
              >
                Lembrete ({preview.reminder})
              </Button>
            </Flex>
            <Flex gap={3} wrap="wrap">
              <StatCard
                label="Aptos com e-mail"
                value={preview.eligible}
                color="abraPurple.500"
                icon="lni lni-users"
                flex="1"
                minW="150px"
              />
              <StatCard
                label="Já responderam"
                value={preview.answered}
                color="abraGreen.500"
                icon="lni lni-checkmark-circle"
                flex="1"
                minW="150px"
              />
              <StatCard
                label="Sem e-mail"
                value={preview.missing_email}
                color="orange.500"
                icon="lni lni-envelope"
                flex="1"
                minW="150px"
              />
            </Flex>
            {preview.recently_sent > 0 && (
              <Text variant="caption">
                {preview.recently_sent} pessoa(s) receberam um link nas últimas 24 horas e ainda não podem receber
                lembrete.
              </Text>
            )}
            {count === 0 && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                Nenhum destinatário para esta ação agora.
              </Alert>
            )}
            {errors.mode && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {errors.mode}
              </Alert>
            )}
            <FormField label="Assunto" error={errors.subject} isRequired>
              <Input value={subject} onChange={(event) => setSubject(event.target.value)} maxLength={255} />
            </FormField>
            <FormField
              label="Corpo do e-mail"
              error={errors.body}
              isRequired
              helperText="Use #PRIMEIRO_NOME, #NOME_EVENTO e #LINK_PESQUISA. O link é individual."
            >
              <Textarea value={body} onChange={(event) => setBody(event.target.value)} rows={9} maxLength={10000} />
            </FormField>
            <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Exemplo para {SAMPLE_RECIPIENT_NAME}
              </Text>
              <Text fontWeight="semibold" mb={3}>
                {sampleText(subject)}
              </Text>
              <Text whiteSpace="pre-wrap" fontSize="sm">
                {sampleText(body)}
              </Text>
            </Box>
          </Flex>
        )}
      </FormModal>
      <ConfirmDialog
        isOpen={confirm.isOpen}
        onClose={confirm.onClose}
        onConfirm={handleDispatch}
        action="send"
        title={mode === 'initial' ? 'Confirmar envio da pesquisa' : 'Confirmar lembrete da pesquisa'}
        description={`Enviar ${count} e-mail(s) da pesquisa para participantes do evento? Esta ação não pode ser desfeita.`}
        confirmLabel="Enviar"
        isLoading={sending}
      />
    </>
  )
}
